import type { IconName } from "./icons";

export type TravelMode = "plane" | "train" | "bus" | "car";

export const TRAVEL_MODES: readonly TravelMode[] = [
  "plane",
  "train",
  "bus",
  "car",
] as const;

export const TRAVEL_MODE_ICON: Record<TravelMode, string> = {
  plane: "✈️",
  train: "🚆",
  bus: "🚌",
  car: "🚗",
};

export const TRAVEL_MODE_ICON_NAME: Record<TravelMode, IconName> = {
  plane: "plane",
  train: "train",
  bus: "bus",
  car: "car",
};

export type Hotel = {
  name?: string;
  address?: string;
  googleMapsUrl?: string;
  checkIn?: string;
  checkOut?: string;
  price?: number;
  currency?: string;
  lat?: number;
  lon?: number;
};

export type TravelDetails = {
  carrier?: string;
  ticketRef?: string;
  departureTime?: string;
  arrivalTime?: string;
  price?: number;
  currency?: string;
};

export type Place = {
  id: string;
  name: string;
  lat?: number;
  lon?: number;
};

export type DayLegMode = "walk" | "transit";

export type RouteLeg = {
  fromId: string;
  toId: string;
  mode: DayLegMode;
  durationMin: number;
  distanceKm: number;
  polyline?: [number, number][];
};

export type DayRoute = {
  orderedPlaceIds: string[];
  legs: RouteLeg[];
  roundtripHotel?: { name: string };
};

/**
 * A dated sub-stop inside a city stay — a day trip to Versailles while you're in Paris.
 *
 * This is the *wire* shape: it travels inside `Stop.excursions` in the Worker payload, which
 * is how an excursion authored here reaches Ruta. Ruta's own device-authored excursions use a
 * superset of this type (see `Ruta/src/lib/local-types.ts`) — so changing this changes both.
 *
 * `parentStopId` and `city` are deliberately absent: both are derivable from the enclosing
 * stop, and storing them twice invites drift. Ruta injects them when it flattens the trip.
 *
 * `dateTo` is optional and usually omitted — a one-day activity. Ruta's `spansToday` reads a
 * missing `dateTo` as a single day, so don't "fix" it by defaulting it to `dateFrom`.
 */
export type Excursion = {
  id: string;
  name: string;
  dateFrom?: string;
  dateTo?: string;
  /** "HH:MM". Orders this against the day's departure for the amber slot on Ruta's Now screen. */
  timeFrom?: string;
  /** "HH:MM". Optional even when `timeFrom` is set — plenty of plans have no end time. */
  timeTo?: string;
  lat?: number;
  lon?: number;
  /** An explicit link, for places OSM can't geocode. Independent of `lat`/`lon`; either may exist alone. */
  mapsUrl?: string;
  notes?: string;
  createdAt: number;
  updatedAt: number;
};

export type Stop = {
  id: string;
  name: string;
  city: string;
  lat: number;
  lon: number;
  dateFrom?: string;
  dateTo?: string;
  arriveBy?: TravelMode;
  departBy?: TravelMode;
  notes?: string;
  hotel?: Hotel;
  arriveDetails?: TravelDetails;
  departDetails?: TravelDetails;
  places?: Place[];
  dayRoute?: DayRoute;
  excursions?: Excursion[];
};

/**
 * Set once a trip has been shared, which is what keeps its link stable across edits.
 * `editToken` is a write capability — it must never leave this machine, so it is excluded
 * from `stops.serialize()` (the export path).
 */
export type ShareRef = {
  id: string;
  editToken: string;
};

export type TripState = {
  /** Stable identity of this trip; used to key it in the local trip library. */
  id: string;
  /** ISO timestamp of when this trip was created. Excluded from `serialize()` (stays local). */
  createdAt: string;
  stops: Stop[];
  tripName?: string;
  share?: ShareRef;
};

export type NominatimSearchResult = {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  name?: string;
  type?: string;
  class?: string;
  address?: NominatimAddress;
};

export type NominatimReverseResult = {
  lat: string;
  lon: string;
  display_name: string;
  address?: NominatimAddress;
};

export type NominatimAddress = {
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  municipality?: string;
  county?: string;
  state?: string;
  country?: string;
  country_code?: string;
};
